import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  Box,
  Grid,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  ThemeProvider,
} from "@material-ui/core";

import * as azure from "@azure/storage-blob";
import {
  AiStructuredDataModelResultDTO,
  GroopItApiClient,
  GroopItClient,
} from "../../../groopit-js/client";
import { groopTheme } from "../../styles/MaterialGroopTheme";
import { AiLoadingComponent } from "./AiLoadingComponent";
import { TextOrUrlClickedPopover } from "./TextOrUrlClickedPopover";
import { CountdownTimer } from "./CountdownTimer";
import { InsightsIcon } from "../../Icons/InsightsIcon";
import NavigateBeforeOutlinedIcon from "@material-ui/icons/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@material-ui/icons/NavigateNextOutlined";
import { useSharingAll } from "./Store/SharingAllStore";
import { HelpPopover } from "../../Controls/HelpPopover";
import { FromJSCollection } from "../../Utilities/ClientJSCollectionParser";

interface ExternalDataToGroupitProps {
  aiExtractionComplete: boolean;
  aiResponseError: string;
  assignmentId: string;
  extractedDataModelIndex: number;
  extractedDataModels: AiStructuredDataModelResultDTO[];
  hasExtractedDataModels: boolean;
  extractionSourceValue?: string;
  groopId: string;
  returnUrl: string;
  prompts: { Description: string; AIPrompt: string }[];
}

const textSource = "text";

const GroopitClient = new GroopItClient({});
const GroopitApiClient = new GroopItApiClient({});

const CreatedStatusCode = 201;
const changeExtractedDataModelFormName = "changeExtractedDataModelForm";
const fileUploadFormName = "fileUploadForm";
const shareAllFormName = "shareAllForm";
const textUploadFormName = "textUploadForm";
const uriUploadFormName = "uriUploadForm";

const countdownTimerStartInSeconds = 20;

const FileLink = (props: {
  extension: string;
  mimetype: string;
  disabled: boolean;
  onFileSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { extension, mimetype, disabled, onFileSelected } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Link
      className={
        disabled ? "cursor-not-allowed underline" : "cursor-pointer underline"
      }
      onClick={() => (disabled ? null : inputRef?.current?.click())}
    >
      {extension}
      <input
        type="file"
        title={extension}
        className="hidden"
        ref={inputRef}
        onChange={(e) => onFileSelected(e)}
        accept={mimetype}
      />
    </Link>
  );
};

export const ExternalDataToGroupit = (props: ExternalDataToGroupitProps) => {
  const [aiInsightsIconClicked, setAiInsightsIconClicked] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    props.aiResponseError,
  );
  const [fileUploadInProgress, setFileUploadInProgress] =
    useState<boolean>(false);
  const [fileUploadProgress, setFileUploadProgress] = useState<number>(0);
  const [fileName, setFileName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showExtractionComplete, setShowExtractionComplete] = useState<boolean>(
    props.aiExtractionComplete,
  );
  const [modeName, setModeName] = useState<string>("Single Extraction");

  const sharingAll = useSharingAll();

  const [
    showTextInputAreaForUsersWithNoClipboardAvailable,
    setShowTextInputAreaForUsersWithNoClipboardAvailable,
  ] = useState<boolean>(false);
  const [textLinkedPopoverAnchorEl, setTextLinkedPopoverAnchorEl] =
    useState<null | HTMLElement>(null);
  const [unstructuredData, setUnstructuredData] = useState<string>("");
  const [uploadIdentifier, setUploadIdentifier] = useState<string>("");
  const [uri, setUri] = useState<string>("");

  const [extractedDataModelIndex, setExtractedDataModelIndex] =
    useState<number>(props.extractedDataModelIndex);

  const [clipboardAvailable, setClipboardAvailable] = useState<boolean>(false);

  const [generateMultipleModels, setGenerateMultipleModels] =
    useState<boolean>(false);
  const [generateFromGroupedData, setGenerateFromGroupedData] =
    useState<boolean>(false);

  const [bulkImport, setbulkImport] = useState(false);

  const handleSingleModelSelected = () => {
    setGenerateMultipleModels(false);
    setGenerateFromGroupedData(false);
    setbulkImport(false);
  };
  const handleMultipleModelsSelected = () => {
    setGenerateMultipleModels(true);
    setGenerateFromGroupedData(false);
    setbulkImport(false);
  };
  const handleMultipleGroupedModelsSelected = () => {
    setGenerateMultipleModels(true);
    setGenerateFromGroupedData(true);
    setbulkImport(false);
  };
  const handleBulkImportSelected = () => {
    setbulkImport(true);
  };

  const checkClipboardAvailability = async () => {
    try {
      if (!navigator.clipboard) {
        setClipboardAvailable(false);
        return;
      }

      await navigator.clipboard.readText();
      setClipboardAvailable(true);
    } catch (error) {
      setClipboardAvailable(false);
    }
  };

  useEffect(() => {
    checkClipboardAvailability();
  }, []);

  const createFromUnstructuredDataEndpointUrl = useMemo(
    () =>
      `/Groopit/${GroopitClient.populateFromUnstructuredData.name}/${
        props.assignmentId
      }?returnUrl=${encodeURIComponent(props.returnUrl)}`,
    [props.assignmentId, props.returnUrl],
  );

  const createFromFileEndpointUrl = useMemo(
    () =>
      `/Groopit/${GroopitClient.populateFromFile.name}/${
        props.assignmentId
      }?returnUrl=${encodeURIComponent(props.returnUrl)}`,
    [props.assignmentId, props.returnUrl],
  );

  const createFromUriEndpointUrl = useMemo(
    () =>
      `/Groopit/${GroopitClient.populateFromUri.name}/${
        props.assignmentId
      }?returnUrl=${encodeURIComponent(props.returnUrl)}`,
    [props.assignmentId, props.returnUrl],
  );

  const shareAllEndpointUrl = useMemo(
    () => "/Groopit/" + GroopitClient.shareAll.name + "/" + props.assignmentId,
    [props.assignmentId],
  );

  const getFromExtractedDataModelEndpointUrl = useMemo(
    () =>
      `/Groopit/${GroopitClient.getFromExtractedDataModel.name}/${
        props.assignmentId
      }/${extractedDataModelIndex}?returnUrl=${encodeURIComponent(
        props.returnUrl,
      )}`,
    [extractedDataModelIndex, props],
  );

  const inputIsUri = useCallback((input: string) => {
    return input.startsWith("http");
  }, []);

  const handleTextChangedByUser = (
    event: React.ChangeEvent<HTMLTextAreaElement> | undefined,
  ) => {
    if (event === undefined) {
      return;
    }

    if (inputIsUri(event.target.value)) {
      setUri(event.target.value);
    } else {
      setUri("");
    }

    setUnstructuredData(event.target.value);
  };

  const handleOnSubmit = () => {
    setLoading(true);
  };

  const handleAiInsightsIconClick = () => {
    setAiInsightsIconClicked(true);
    setShowExtractionComplete(false);
    setErrorMessage("");
  };

  const handleOnTryUseClipboardContents = (clipboardText: string) => {
    setTextLinkedPopoverAnchorEl(null);

    determineFormAndSubmit(clipboardText);
  };

  // eslint-disable-next-line @typescript-eslint/ban-types
  const clickAwayHandler = (event: {}) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    event.stopPropagation();

    setTextLinkedPopoverAnchorEl(null);
  };

  const handleTextLinkClicked = (
    event:
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
      | React.MouseEvent<HTMLSpanElement, MouseEvent>,
  ) => {
    if (clipboardAvailable) {
      setTextLinkedPopoverAnchorEl(event.currentTarget);
      return;
    }

    setShowTextInputAreaForUsersWithNoClipboardAvailable(true);
  };

  const handleOnNavigateBeforeClicked = () => {
    setExtractedDataModelIndex(
      (extractedDataModelIndex) => extractedDataModelIndex - 1,
    );
  };

  const handleOnNavigateNextClicked = () => {
    setExtractedDataModelIndex(
      (extractedDataModelIndex) => extractedDataModelIndex + 1,
    );
  };

  // Handles file upload logic, separated from the event handler.
  async function uploadFile(
    fileToUpload: File,
    onPendingUploadCreated?: (image: string) => void,
    onSuccess?: (image: string) => void,
    onError?: (httpStatus: number) => void,
  ) {
    setFileUploadInProgress(true);
    setFileUploadProgress(0);

    const { upload, image } = await GroopitApiClient.createAiPendingUpload(
      fileToUpload.type,
    );
    onPendingUploadCreated?.(image);
    const uploader = new azure.BlockBlobClient(upload);
    const response = await uploader.uploadData(fileToUpload, {
      onProgress: (e) => {
        setFileUploadProgress((99 * e.loadedBytes) / fileToUpload.size);
      },
      blobHTTPHeaders: { blobContentType: fileToUpload.type },
    });
    setFileUploadProgress(100);
    setFileUploadInProgress(false);
    if (response._response.status === CreatedStatusCode) {
      onSuccess?.(image);
    } else {
      onError?.(response._response.status);
    }
  }

  // Prompts the user to select a file. Immediately uploads the selected file and makes the call to extract data.
  const handleOnFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const fileToUpload = event.target.files?.[0];
    if (!fileToUpload) {
      return;
    }

    setFileName(fileToUpload.name);
    setLoading(true);

    await uploadFile(
      fileToUpload,
      (image) => {
        setUploadIdentifier(image ?? "");
      },
      () => {
        // success;
        setUnstructuredData("");
        submitForm(fileUploadFormName);
      },
      () => {
        // error
        setLoading(false);
        setErrorMessage(
          "There was an error uploading the file. Please try again.",
        );
      },
    );
  };

  // Handles the case when the user selects a CSV file for bulk import.
  const uploadBulkImportSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const fileToUpload = event.target.files?.[0];
    if (!fileToUpload) {
      return;
    }

    setLoading(true);

    await uploadFile(
      fileToUpload,
      undefined,
      (fileid) => {
        // success;
        window.location.href = `/Groopit/BulkImport?assignmentId=${props.assignmentId}&uploadIdentifier=${fileid}`;
      },
      () => {
        // error
        setLoading(false);
        setErrorMessage(
          "There was an error uploading the file. Please try again.",
        );
      },
    );
  };

  const submitForm = useCallback((formName: string) => {
    setShowExtractionComplete(false);
    setErrorMessage("");

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    document.forms[formName].submit();
  }, []);

  const determineFormAndSubmit = useCallback(
    (input: string) => {
      if (inputIsUri(input)) {
        setUri(input);
        submitForm(uriUploadFormName);
      } else {
        setUnstructuredData(input);
        submitForm(textUploadFormName);
      }

      setLoading(true);
    },
    [inputIsUri, submitForm],
  );

  const getFromExtractedModel = useCallback(() => {
    submitForm(changeExtractedDataModelFormName);
  }, [submitForm]);

  const handleOnSubmitButtonClick = () => {
    determineFormAndSubmit(unstructuredData);
  };

  useEffect(() => {
    // Ensure that getFromExtractedModel does not run on the initial render.
    if (extractedDataModelIndex !== props.extractedDataModelIndex) {
      getFromExtractedModel();
    }
  }, [
    extractedDataModelIndex,
    getFromExtractedModel,
    props.extractedDataModelIndex,
  ]);

  useEffect(() => {
    if (sharingAll) {
      submitForm(shareAllFormName);
    }
  }, [sharingAll, submitForm]);

  return (
    <>
      <ThemeProvider theme={groopTheme}>
        <Box color="primary.dark">
          <form
            id={changeExtractedDataModelFormName}
            action={getFromExtractedDataModelEndpointUrl}
            method="POST"
          >
            <textarea
              hidden
              name="extractedDataModels"
              id="extractedDataModels"
              value={JSON.stringify(props.extractedDataModels)}
              readOnly
            />
            <textarea
              hidden
              name="extractionSource.Value"
              value={props.extractionSourceValue}
              readOnly
            />
          </form>
          <form
            id={fileUploadFormName}
            className="text-sm"
            action={createFromFileEndpointUrl}
            method="POST"
            onSubmit={handleOnSubmit}
          >
            <textarea
              hidden
              name="uploadIdentifier"
              value={uploadIdentifier}
              readOnly
            />
            <textarea
              hidden
              name="generateMultipleModels"
              value={generateMultipleModels ? "True" : "False"}
              readOnly
            />
            <textarea
              hidden
              name="generateFromGroupedData"
              value={generateFromGroupedData ? "True" : "False"}
              readOnly
            />
            <textarea hidden name="fileName" value={fileName} readOnly />
            <Grid
              container
              spacing={0}
              className="my-4 mr-4"
              alignItems="center"
              direction="row"
            >
              {sharingAll && (
                <>
                  <Grid item xs={1}>
                    <AiLoadingComponent
                      loading={true}
                      shouldHover={true}
                      showAiIcon={false}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={11}
                    container
                    alignItems="center"
                    direction="row"
                  >
                    <Grid item className="ml-4">
                      <div className="text-sm">Sharing...</div>
                    </Grid>
                  </Grid>
                </>
              )}
              {!sharingAll && (
                <>
                  <Grid item xs={1}>
                    <AiLoadingComponent
                      loading={loading}
                      clicked={handleAiInsightsIconClick}
                      shouldHover={true}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={11}
                    container
                    alignItems="center"
                    direction="row"
                    spacing={0}
                  >
                    <Grid item hidden={!loading} className="ml-4">
                      <div className="text-sm">
                        Extracting (
                        <CountdownTimer
                          running={loading}
                          timerStart={countdownTimerStartInSeconds}
                        />
                        )
                      </div>
                    </Grid>
                    {showExtractionComplete &&
                      (errorMessage === "" ? (
                        <>
                          {!props.hasExtractedDataModels && (
                            <Grid item className="ml-4">
                              Extraction complete
                            </Grid>
                          )}
                          {props.hasExtractedDataModels && (
                            <>
                              <span className="text-sm ml-2">
                                Extraction Complete |
                              </span>
                              <Grid
                                item
                                xs={7}
                                container
                                alignItems="center"
                                justifyContent="space-evenly"
                                direction="row"
                              >
                                <>
                                  <Grid
                                    item
                                    xs={1}
                                    container
                                    justifyContent="flex-start"
                                    alignItems="center"
                                  >
                                    <NavigateBeforeOutlinedIcon
                                      aria-label="navigate before"
                                      className={`cursor-pointer ${
                                        extractedDataModelIndex == 0
                                          ? "invisible"
                                          : ""
                                      }`}
                                      fontSize="medium"
                                      onClick={handleOnNavigateBeforeClicked}
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    xs={10}
                                    container
                                    justifyContent="center"
                                    alignItems="center"
                                  >
                                    <div>
                                      Review{" "}
                                      <strong>
                                        {extractedDataModelIndex + 1} of{" "}
                                        {props.extractedDataModels.length}
                                      </strong>{" "}
                                      extractions
                                    </div>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={1}
                                    container
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    className="!-mr-2.5"
                                  >
                                    <NavigateNextOutlinedIcon
                                      aria-label="navigate next"
                                      className={`cursor-pointer ${
                                        extractedDataModelIndex <
                                        props.extractedDataModels.length - 1
                                          ? ""
                                          : "invisible"
                                      }`}
                                      fontSize="medium"
                                      onClick={handleOnNavigateNextClicked}
                                    />
                                  </Grid>
                                </>
                              </Grid>
                            </>
                          )}
                        </>
                      ) : (
                        <Grid item className="ml-4">
                          <Box color="secondary.main">
                            No relevant intelligence was found or extracted.
                            <br />
                            Please manually enter relevant data.
                          </Box>
                        </Grid>
                      ))}
                    {aiInsightsIconClicked && !loading && (
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        className="ml-4 gap-2"
                        wrap="nowrap"
                      >
                        <Select
                          className="text-app-teal rounded-full border-app-teal border-1 pl-3 text-sm"
                          disableUnderline
                          value={modeName}
                          onChange={(e) =>
                            setModeName(e.target.value as string)
                          }
                        >
                          <MenuItem
                            onClick={handleSingleModelSelected}
                            value="Single Extraction"
                          >
                            Single Extraction
                          </MenuItem>
                          <MenuItem
                            onClick={handleMultipleModelsSelected}
                            value="Extract Multiple | header"
                          >
                            Extract Multiple | header
                          </MenuItem>
                          <MenuItem
                            onClick={handleMultipleGroupedModelsSelected}
                            value="Extract Multiple | rows"
                          >
                            Extract Multiple | rows
                          </MenuItem>
                          <MenuItem
                            onClick={handleBulkImportSelected}
                            value="Bulk Data Import"
                          >
                            Bulk Data Import
                          </MenuItem>
                        </Select>
                        <span>
                          {" "}
                          from{" "}
                          {bulkImport ? (
                            <>
                              <FileLink
                                extension="csv"
                                mimetype=".csv,application/csv"
                                disabled={loading}
                                onFileSelected={uploadBulkImportSelected}
                              />
                            </>
                          ) : (
                            <>
                              <Link
                                className={
                                  loading
                                    ? "cursor-not-allowed underline"
                                    : "cursor-pointer underline"
                                }
                                onClick={(event) =>
                                  handleTextLinkClicked(event)
                                }
                              >
                                clipboard
                                <TextOrUrlClickedPopover
                                  anchorEl={textLinkedPopoverAnchorEl}
                                  clickAwayHandler={(event) =>
                                    clickAwayHandler(event)
                                  }
                                  processClipboardContents={
                                    handleOnTryUseClipboardContents
                                  }
                                  source={textSource}
                                ></TextOrUrlClickedPopover>
                              </Link>
                              {", "}
                              <FileLink
                                extension="document"
                                mimetype=".pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                disabled={loading}
                                onFileSelected={handleOnFileSelected}
                              />
                              {", or "}
                              <FileLink
                                extension="image"
                                mimetype=".jpeg,.jpg,.png,.bmp,.tiff,.heif,image/png,image/jpeg,image/bmp,image/tiff"
                                disabled={loading}
                                onFileSelected={handleOnFileSelected}
                              />
                              .
                            </>
                          )}
                        </span>
                        <HelpPopover
                          popoverId="extraction-prompts"
                          title="Instructions for Extractive AI"
                        >
                          <div>
                            AI will extract information from the chosen source
                            by following these instructions.
                          </div>
                          <br />
                          <ul className="space-y-2">
                            {props.prompts.map((prompt, index) => (
                              <li key={index}>
                                <div className="font-bold">
                                  {prompt.Description}
                                </div>
                                {prompt.AIPrompt && (
                                  <div>{prompt.AIPrompt}</div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </HelpPopover>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        </Box>
        {fileUploadInProgress && (
          <LinearProgress variant="determinate" value={fileUploadProgress} />
        )}
        <form
          id={textUploadFormName}
          onSubmit={handleOnSubmit}
          method="POST"
          action={createFromUnstructuredDataEndpointUrl}
          hidden={loading}
        >
          <div
            hidden={!showTextInputAreaForUsersWithNoClipboardAvailable}
            className="border rounded-lg border-black"
          >
            <Box color="primary.dark" className="text-sm italic p-1">
              Highlight source text. Ctrl-C to copy. Ctrl-V to paste. Click{" "}
              <InsightsIcon onClick={handleOnSubmitButtonClick} />.
            </Box>
            <textarea
              readOnly={loading}
              hidden={!showTextInputAreaForUsersWithNoClipboardAvailable}
              id="external-data"
              name="unstructuredData"
              className="table-component border-b border-l border-r border-gray-400 w-full h-80 p-2 text-sm"
              value={unstructuredData}
              onChange={handleTextChangedByUser}
            />
          </div>
        </form>
        <form
          id={uriUploadFormName}
          method="POST"
          onSubmit={handleOnSubmit}
          action={createFromUriEndpointUrl}
          hidden={loading}
        >
          <textarea hidden name="uri" value={uri} readOnly />
        </form>
        <form
          id={shareAllFormName}
          method="POST"
          onSubmit={handleOnSubmit}
          action={shareAllEndpointUrl}
          hidden={loading}
        >
          <textarea
            hidden
            name="extractedDataModels"
            value={JSON.stringify(props.extractedDataModels)}
            readOnly
          />
        </form>
      </ThemeProvider>
      <hr className="small-divider" />
    </>
  );
};

const externalDataToGroupit = document.querySelector(
  ".external-data-to-groupit",
) as HTMLElement;

if (externalDataToGroupit) {
  ReactDOM.render(
    <ExternalDataToGroupit
      aiExtractionComplete={
        externalDataToGroupit.dataset.aiExtractionComplete?.toLowerCase() ===
        "true"
      }
      aiResponseError={externalDataToGroupit.dataset.aiResponseError as string}
      assignmentId={externalDataToGroupit.dataset.assignmentId as string}
      hasExtractedDataModels={
        externalDataToGroupit.dataset.hasExtractedDataModels === "True"
      }
      extractedDataModelIndex={Number.parseInt(
        externalDataToGroupit.dataset
          .extractedDataModelIndex as unknown as string,
      )}
      extractedDataModels={FromJSCollection(
        externalDataToGroupit.dataset.extractedDataModels as string,
        AiStructuredDataModelResultDTO.fromJS,
      )}
      extractionSourceValue={
        externalDataToGroupit.dataset.extractionSourceValue
          ? externalDataToGroupit.dataset.extractionSourceValue
          : undefined
      }
      groopId={externalDataToGroupit.dataset.groopId as string}
      returnUrl={externalDataToGroupit.dataset.returnUrl as string}
      prompts={JSON.parse(externalDataToGroupit.dataset.prompts as string)}
    ></ExternalDataToGroupit>,
    externalDataToGroupit,
  );
}
